import React, { useState, useEffect } from "react";
import { Table, Row, Col, Collapse, Button, Spinner } from "reactstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import { toast } from "react-toastify";
// componentes
import Pagination from "../../../../components/Paginationv3";
import SectionComponent from "../../../Apps/Personalization/SectionComponent";
import LoadingSkelentonTable from "../../../../components/loadingSkeletonTable";
import WaitExport from "../../../../components/WaitExport";
import ModalWordCloud from "./wordCloud";
// service
import {
  getAsk,
  disableAsk,
} from "../../../../utils/services/dashboardSuperScreen";
// utils
import useDebounce from "../../../../utils/hooks/useDebounce";
import { createXlsx, formatDate } from "./exports/createXlsxQuestion";

export default function TableQuestion(props) {
  const eventId = props.idEvent;
  const transmissionId = props.idDashboard;
  // state de pesquisa
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  // state de paginação
  const [currentIndex, setCurrentIndex] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // força um novo fetch mesmo quando nenhum filtro mudou (botão Refresh)
  const [refreshKey, setRefreshKey] = useState(0);
  // state do collapse de opções (mobile)
  const [isOpen, setIsOpen] = useState(false);
  // state do modal da nuvem de palavras
  const [modalWordCloud, setModalWordCloud] = useState(false);
  // state de exportação
  const [exportInProgress, setExportInProgress] = useState(false);
  const [finishExport, setFinishExport] = useState(false);
  // state de deletar pergunta
  const [deleteData, setDeleteData] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [successDelete, setSuccessDelete] = useState(false);
  const [errorDelete, setErrorDelete] = useState(false);
  // state de carregamento do botão
  const [loading, setLoading] = useState(false);

  const question = props.tableData.question;
  const countData = question.total ?? 0;

  // atualiza os dados de perguntas no state compartilhado do dashboard
  function updateQuestion(values) {
    props.setTableData((state) => ({
      ...state,
      question: { ...state.question, ...values },
    }));
  }

  // inicia a exportação das perguntas em xlsx
  function exportReport() {
    createXlsx({
      eventId,
      transmissionID: transmissionId,
      search: "",
      countData,
      mobileapps: props.mobileapps,
      setFinishExport,
      setExportInProgress,
    });
    setExportInProgress(true);
  }

  // desativa a pergunta selecionada
  async function funcDeleteAsk() {
    setLoading(true);
    try {
      const res = await disableAsk({
        eventId,
        transmissionID: transmissionId,
        askId: deleteData.id,
        mobileapps: props.mobileapps,
      });

      setConfirmDelete(false);
      if (res?.success === true) {
        setSuccessDelete(true);
      } else {
        setErrorDelete(true);
      }
    } catch (error) {
      setConfirmDelete(false);
      setErrorDelete(true);
    } finally {
      setLoading(false);
    }
  }

  // atualiza o termo pesquisado e volta para a primeira página, senão a busca
  // sairia com o offset da página em que o usuário estava
  function handleSearch(value) {
    setSearch(value.toLowerCase());
    setCurrentIndex(1);
  }

  // volta a listagem para o estado inicial. O incremento do refreshKey dispara o
  // refetch pelo useEffect, já com o state atualizado, em vez de chamar o fetch
  // aqui com os valores antigos do render
  function resetPagination() {
    setSearch("");
    setItemsPerPage(10);
    setCurrentIndex(1);
    setRefreshKey((key) => key + 1);
  }

  useEffect(() => {
    // enquanto o debounce não fecha, search e debouncedSearch divergem e o fetch
    // sairia com o termo antigo. O próprio debounce dispara este efeito de novo
    if (search !== debouncedSearch) return;
    if (!props.mobileapps) return;

    // ignora respostas de requisições antigas (página ou pesquisa anterior)
    let ignore = false;

    updateQuestion({ loading: true });
    getAsk({
      eventId,
      transmissionID: transmissionId,
      pageSize: itemsPerPage,
      offset: currentIndex - 1,
      search: debouncedSearch,
      mobileapps: props.mobileapps,
    })
      .then((res) => {
        if (ignore) return;

        if (res?.success) {
          updateQuestion({ loading: false, data: res.data, total: res.total });
        } else {
          updateQuestion({ loading: false });
          toast.error("Erro ao buscar perguntas.");
        }
      })
      .catch(() => {
        if (ignore) return;

        updateQuestion({ loading: false });
        toast.error("Erro ao buscar perguntas.");
      });

    return () => {
      ignore = true;
    };
  }, [
    debouncedSearch,
    itemsPerPage,
    currentIndex,
    props.mobileapps,
    refreshKey,
  ]);

  return (
    <div>
      <WaitExport
        isOpen={exportInProgress}
        name="Relatório"
        preposicao="dos"
        finishExport={finishExport}
        setFinishExport={setFinishExport}
        setExportInProgress={setExportInProgress}
      />

      {modalWordCloud && (
        <ModalWordCloud
          isOpen={modalWordCloud}
          toggle={() => setModalWordCloud(!modalWordCloud)}
          eventId={eventId}
          transmissionID={transmissionId}
          type={"asks"}
          mobileapps={props.mobileapps}
        />
      )}

      {confirmDelete ? (
        <SweetAlert
          title={`Deseja deletar a pergunta ${deleteData.msg}?`}
          warning
          showConfirm={false}
          showCancel={false}
        >
          <div className="d-flex flex-column align-items-center">
            <p className="mb-2">Você não poderá reverter essa ação!</p>
            <div className="d-flex">
              <Button
                color={loading ? "secondary" : "success"}
                className="waves-effect waves-light"
                style={{ width: loading ? "auto" : 100, marginRight: 10 }}
                disabled={loading}
                onClick={() => funcDeleteAsk()}
              >
                {loading && <Spinner size="sm" className="me-1" />}
                Confirmar
              </Button>
              <Button
                color="danger"
                className="waves-effect waves-light"
                style={{ width: 100 }}
                disabled={loading}
                onClick={() => setConfirmDelete(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </SweetAlert>
      ) : null}

      {successDelete ? (
        <SweetAlert
          success
          title="Deletada"
          onConfirm={() => {
            setSuccessDelete(false);
            setRefreshKey((key) => key + 1);
          }}
        >
          Pergunta deletada com sucesso
        </SweetAlert>
      ) : null}

      {errorDelete ? (
        <SweetAlert error title="Erro" onConfirm={() => setErrorDelete(false)}>
          Erro ao deletar a pergunta.
        </SweetAlert>
      ) : null}

      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          margin: "10px 0",
        }}
      >
        <form
          className="app-search d-none d-lg-block events-input-search"
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder="Pesquisar..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <span className="bx bx-search-alt" />
          </div>
        </form>

        <div className="mobile-btn-section" style={{ width: "100%" }}>
          <Row className="mobile-btns-top">
            <form
              className="app-search"
              style={{ padding: "0px" }}
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Pesquisar..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <span className="bx bx-search-alt" />
              </div>
            </form>
          </Row>

          {question.data.length > 0 && (
            <>
              <SectionComponent
                sectionName="Opções"
                setIsOpen={setIsOpen}
                isOpen={isOpen}
              />
              <Collapse isOpen={isOpen}>
                <Row className="mobile-btns-top">
                  <div onClick={() => setModalWordCloud(true)}>
                    Nuvem de Palavras
                  </div>
                  <div onClick={() => exportReport()}>Exportar Perguntas</div>
                </Row>
              </Collapse>
            </>
          )}
        </div>

        <div className="header-btns-container">
          {question.data.length > 0 && (
            <>
              <Button
                color="success"
                className="btn-rounded waves-effect waves-light events-button-add"
                onClick={() => setModalWordCloud(true)}
              >
                Nuvem de Palavras
              </Button>

              <Button
                color="success"
                className="btn-rounded waves-effect waves-light events-button-add"
                onClick={() => exportReport()}
              >
                Exportar Perguntas
              </Button>
            </>
          )}
        </div>
      </div>

      <div
        style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
      >
        <button
          type="button"
          className="btn-reset-table border-0"
          onClick={() => resetPagination()}
        >
          <i className="mdi mdi-refresh" style={{ marginRight: 5 }} />
          Refresh
        </button>
      </div>

      <div className="table-responsive bg-white">
        <Table className="table mb-0">
          <thead className="table-light">
            <tr>
              <th>ID do Participante</th>
              <th>Nome</th>
              <th>Pergunta</th>
              <th>Data e hora</th>
              <th>Ação</th>
            </tr>
          </thead>

          <tbody>
            {question.loading ? (
              <LoadingSkelentonTable lines={3} cols={5} />
            ) : (
              question.data.map((e) => (
                <tr key={e.id}>
                  <td>{e.userID}</td>
                  <td>{e.name}</td>
                  <td>{e.msg}</td>
                  <td>{formatDate(e.date)}</td>
                  <td>
                    <i
                      role="button"
                      title="Deletar pergunta"
                      className="mdi mdi-trash-can text-danger font-size-17"
                      onClick={() => {
                        setDeleteData(e);
                        setConfirmDelete(true);
                      }}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <Row style={{ marginTop: 20 }}>
        <Col md={12}>
          <Pagination
            data={question.data}
            search={search}
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
            countData={countData}
            maxItemPerPage={itemsPerPage}
            setMaxItemPerPage={setItemsPerPage}
            maxPageButtonsToShow={3}
          />
        </Col>
      </Row>
    </div>
  );
}
